/**
 * Promo-code matrix editor for a single campaign.
 *
 * NEW SCHEMA:
 *  rule = {
 *    tiers:      string[]                              // ["*"] = any; ["GOLD_TIER","PLATINUM_TIER"] = both
 *    conditions: [{ operator: ">"|"<", inactive_days: int }, ...]   // 1 or 2 (AND semantics)
 *    promo_code: string
 *  }
 *
 * UI choices:
 *  - Native <select> for operator (fixes the persistent Radix dropdown bug we
 *    saw in the Dialog).
 *  - Checkbox popover for multi-tier (uses shadcn Popover, safe inside Dialog).
 *  - "+ AND another condition" button appears when only 1 condition exists.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Plus, Trash2, Clock, ChevronDown } from "lucide-react";

const TIERS = ["BLUE_TIER", "SILVER_TIER", "GOLD_TIER", "PLATINUM_TIER", "ELITE_TIER"];

// ---- helpers ----
function migrateRule(r) {
  // Coerce legacy shape → new shape for the editor state.
  if (!r) return { tiers: ["*"], conditions: [{ operator: ">", inactive_days: 30 }], promo_code: "" };
  const tiers = r.tiers && r.tiers.length ? r.tiers : (r.tier ? [r.tier] : ["*"]);
  const conditions = r.conditions && r.conditions.length
    ? r.conditions.map((c) => ({ operator: c.operator, inactive_days: c.inactive_days }))
    : (r.operator ? [{ operator: r.operator, inactive_days: r.inactive_days ?? 30 }]
                  : [{ operator: ">", inactive_days: 30 }]);
  return { tiers, conditions, promo_code: r.promo_code || "" };
}

function tierSummary(tiers) {
  if (!tiers || tiers.length === 0) return "Any tier";
  if (tiers.includes("*")) return "Any tier";
  if (tiers.length === 1) return tiers[0].replace("_TIER", "");
  return `${tiers.length} tiers`;
}

// ---- Multi-tier selector ----
function TierSelector({ value, onChange, testIdPrefix }) {
  const isAny = !value || value.length === 0 || value.includes("*");
  const [open, setOpen] = useState(false);

  const toggleTier = (t) => {
    let next;
    if (value.includes(t)) {
      next = value.filter((v) => v !== t);
    } else {
      next = [...value.filter((v) => v !== "*"), t];
    }
    if (next.length === 0) next = ["*"];
    onChange(next);
  };

  const toggleAny = () => {
    onChange(isAny ? [TIERS[0]] : ["*"]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="glass-input h-8 flex items-center justify-between px-2 text-xs w-full"
          data-testid={`${testIdPrefix}-trigger`}
        >
          <span className={isAny ? "text-slate-500 italic" : "font-mono"}>
            {tierSummary(value)}
          </span>
          <ChevronDown className="w-3 h-3 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <label className="flex items-center gap-2 py-1 cursor-pointer border-b border-slate-100 mb-1 pb-2"
               data-testid={`${testIdPrefix}-any`}>
          <Checkbox checked={isAny} onCheckedChange={toggleAny} />
          <span className="text-xs italic text-slate-600">Any tier</span>
        </label>
        {TIERS.map((t) => (
          <label key={t} className="flex items-center gap-2 py-1 cursor-pointer"
                 data-testid={`${testIdPrefix}-${t}`}>
            <Checkbox
              checked={!isAny && value.includes(t)}
              disabled={isAny}
              onCheckedChange={() => toggleTier(t)}
            />
            <span className="text-xs font-mono">{t}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

// ---- Rule editor row ----
function RuleRow({ rule, idx, onChange, onRemove }) {
  const updateTiers = (tiers) => onChange({ ...rule, tiers });
  const updateCond = (ci, patch) => {
    const conditions = rule.conditions.map((c, i) => i === ci ? { ...c, ...patch } : c);
    onChange({ ...rule, conditions });
  };
  const addCondition = () => {
    onChange({ ...rule, conditions: [...rule.conditions, { operator: "<", inactive_days: 60 }] });
  };
  const removeCondition = (ci) => {
    onChange({ ...rule, conditions: rule.conditions.filter((_, i) => i !== ci) });
  };

  return (
    <div className="glass-solid p-3 rounded-lg space-y-2" data-testid={`promo-rule-${idx}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-2">
          {/* Tier row */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold text-slate-500 uppercase w-14 shrink-0">Tier</span>
            <div className="flex-1">
              <TierSelector
                value={rule.tiers || []}
                onChange={updateTiers}
                testIdPrefix={`promo-rule-${idx}-tier`}
              />
            </div>
          </div>
          {/* Conditions */}
          {rule.conditions.map((c, ci) => (
            <div key={ci} className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-slate-500 uppercase w-14 shrink-0">
                {ci === 0 ? "Days" : "AND"}
              </span>
              {/* Native select — reliable inside Dialog */}
              <select
                value={c.operator}
                onChange={(e) => updateCond(ci, { operator: e.target.value })}
                className="glass-input h-8 px-2 text-sm font-mono w-16"
                data-testid={`promo-rule-${idx}-cond-${ci}-op`}
              >
                <option value=">">&gt;</option>
                <option value="<">&lt;</option>
              </select>
              <Input
                type="number"
                min="0"
                value={c.inactive_days}
                onChange={(e) => updateCond(ci, { inactive_days: Number(e.target.value) })}
                className="glass-input h-8 font-mono text-center w-24"
                data-testid={`promo-rule-${idx}-cond-${ci}-days`}
              />
              <span className="text-xs text-slate-500">days inactive</span>
              {rule.conditions.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeCondition(ci)}
                  className="text-red-500 hover:text-red-700 ml-auto"
                  data-testid={`promo-rule-${idx}-cond-${ci}-remove`}
                  title="Remove this condition"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          {rule.conditions.length < 2 && (
            <button
              type="button"
              onClick={addCondition}
              className="text-xs text-[#0A84FF] hover:underline ml-16"
              data-testid={`promo-rule-${idx}-add-cond`}
            >
              + AND another condition
            </button>
          )}
          {/* Promo code */}
          <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
            <span className="text-[10px] font-semibold text-slate-500 uppercase w-14 shrink-0">Code</span>
            <Input
              value={rule.promo_code}
              onChange={(e) => onChange({ ...rule, promo_code: e.target.value })}
              className="glass-input h-8 font-mono uppercase flex-1"
              placeholder="e.g. COMEBACK50"
              data-testid={`promo-rule-${idx}-code`}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-500 hover:text-red-700 shrink-0 mt-1"
          data-testid={`promo-rule-${idx}-remove`}
          title="Delete this rule"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---- Dialog ----
export default function PromoConfigDialog({ open, onClose, campaign }) {
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!open || !campaign) return;
    setLoading(true);
    api.get(`/leads/campaigns/${campaign.id}/promo-config`)
      .then((r) => setRules(((r.data?.rules) || []).map(migrateRule)))
      .catch(() => setRules([]))
      .finally(() => setLoading(false));
    api.get(`/leads/campaigns/${campaign.id}/promo-config/history`)
      .then((r) => setHistory(r.data?.versions || []))
      .catch(() => setHistory([]));
  }, [open, campaign]);

  const addRule = () => setRules([
    ...rules,
    { tiers: ["*"], conditions: [{ operator: ">", inactive_days: 30 }], promo_code: "" }
  ]);

  const updateRule = (i, next) => setRules(rules.map((r, idx) => idx === i ? next : r));
  const removeRule = (i) => setRules(rules.filter((_, idx) => idx !== i));

  const save = async () => {
    for (const [i, r] of rules.entries()) {
      if (!(r.promo_code || "").trim()) { toast.error(`Rule ${i + 1}: promo code required`); return; }
      if (!r.conditions || r.conditions.length === 0) { toast.error(`Rule ${i + 1}: at least one condition required`); return; }
      for (const [ci, c] of r.conditions.entries()) {
        if (c.inactive_days === "" || c.inactive_days === null || isNaN(Number(c.inactive_days))) {
          toast.error(`Rule ${i + 1} condition ${ci + 1}: days required`); return;
        }
      }
    }
    setSaving(true);
    try {
      await api.post(`/leads/campaigns/${campaign.id}/promo-config`, {
        rules: rules.map((r) => ({
          tiers: r.tiers && r.tiers.length ? r.tiers : ["*"],
          conditions: r.conditions.map((c) => ({
            operator: c.operator,
            inactive_days: Number(c.inactive_days),
          })),
          promo_code: (r.promo_code || "").trim(),
        })),
      });
      toast.success("Promo config saved. New version is active from today.");
      onClose(true);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!campaign) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose(false)}>
      <DialogContent className="glass-card max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="promo-config-dialog">
        <DialogHeader>
          <DialogTitle>
            Promo codes · <span className="font-mono">{campaign.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-slate-500 mb-2">
          Rules evaluate top-to-bottom &mdash; first match wins. A rule matches when the user&apos;s tier is selected <em>AND</em> every condition is satisfied.
        </div>

        <div className="space-y-3 overflow-y-auto pr-1 flex-1">
          {loading ? (
            <div className="text-sm text-slate-500 py-6 text-center">Loading&hellip;</div>
          ) : rules.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-lg">
              No rules yet. Click &ldquo;Add rule&rdquo; below to define promo codes.
            </div>
          ) : (
            rules.map((r, i) => (
              <RuleRow
                key={i}
                idx={i}
                rule={r}
                onChange={(next) => updateRule(i, next)}
                onRemove={() => removeRule(i)}
              />
            ))
          )}
        </div>

        <Button variant="outline" onClick={addRule} className="w-full mt-2 shrink-0" data-testid="promo-add-rule">
          <Plus className="w-3 h-3 mr-1" /> Add rule
        </Button>

        {/* History */}
        <div className="border-t pt-3 mt-3 shrink-0">
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="promo-toggle-history"
          >
            <Clock className="w-3 h-3" /> {showHistory ? "Hide" : "Show"} history ({history.length} version{history.length === 1 ? "" : "s"})
          </button>
          {showHistory && (
            <div className="mt-2 space-y-1 max-h-40 overflow-y-auto text-xs">
              {history.length === 0 && <div className="text-slate-500">No prior versions.</div>}
              {history.map((v, i) => (
                <div key={v.id || i} className="glass-solid p-2 rounded flex justify-between"
                     data-testid={`promo-history-${i}`}>
                  <div>
                    <span className="font-mono">{v.effective_from}</span>
                    {" → "}
                    <span className="font-mono">{v.effective_to || <em className="text-emerald-600 not-italic">active</em>}</span>
                    <span className="text-slate-500 ml-2">· {v.rules?.length || 0} rules</span>
                  </div>
                  <div className="text-slate-500">{v.created_by || "—"}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="promo-save-btn">
            {saving ? "Saving…" : "Save as new version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
